import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useTranslation } from 'react-i18next';

// Define the Team Member type to match our database structure
interface TeamMember {
  id: string;
  name: string;
  role: string;
  photo: string;
  bio: string;
  display_order: number;
  is_active: boolean;
}

const timeline = [
  { year: '2010', event: 'Healing Lotus founded with a vision for holistic wellness in the heart of Plano.' },
  { year: '2014', event: 'Expanded our facilities to offer advanced facial and body treatments.' },
  { year: '2018', event: 'Awarded Best Luxury Spa in Dallas-Fort Worth metro area.' },
  { year: '2023', event: 'Launched exclusive membership program and wellness workshops.' },
  { year: '2025', event: 'Celebrating 15 years with our renovated space and new treatment offerings.' },
];

function About() {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation(['about', 'common']);

  useEffect(() => {
    // Fetch team data from Supabase
    const fetchTeam = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await supabase
          .from('team')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });
          
        if (error) {
          throw error;
        }
        
        setTeam(data || []);
      } catch (err: any) {
        console.error('Error fetching team data:', err);
        setError('Failed to load team data. Please try again later.');
        
        // If data fetch fails, provide fallback data so the page still works
        setTeam([
          {
            id: '1',
            name: 'Olivia Chen',
            role: 'Founder & Lead Therapist',
            photo: 'https://images.pexels.com/photos/4946515/pexels-photo-4946515.jpeg?auto=compress&cs=tinysrgb&w=600',
            bio: 'With 15+ years of experience, Olivia specializes in holistic massage and wellness therapies. Her vision of a truly transformative spa experience brought Healing Lotus to life.',
            display_order: 1,
            is_active: true
          },
          {
            id: '2',
            name: 'Marcus Lee',
            role: 'Aesthetician',
            photo: 'https://images.pexels.com/photos/8460267/pexels-photo-8460267.jpeg?auto=compress&cs=tinysrgb&w=600',
            bio: 'Marcus brings a gentle touch and deep knowledge of skincare to every facial treatment. His approach combines Eastern and Western techniques for optimal results.',
            display_order: 2,
            is_active: true
          },
          {
            id: '3',
            name: 'Sofia Martinez',
            role: 'Spa Manager',
            photo: 'https://images.pexels.com/photos/3767392/pexels-photo-3767392.jpeg?auto=compress&cs=tinysrgb&w=600',
            bio: 'Sofia ensures every guest enjoys a seamless, luxurious experience from start to finish. Her attention to detail makes every visit special.',
            display_order: 3,
            is_active: true
          },
          {
            id: '4',
            name: 'David Kim',
            role: 'Massage Therapist',
            photo: 'https://images.pexels.com/photos/8942991/pexels-photo-8942991.jpeg?auto=compress&cs=tinysrgb&w=600',
            bio: 'David is known for his deep tissue techniques and calming presence. He specializes in therapeutic massage that addresses chronic pain and tension.',
            display_order: 4,
            is_active: true
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeam();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[#232347]/60 z-10"></div>
        <div className="relative z-20 text-center px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-serif">{t('about:hero.title')}</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">{t('about:hero.subtitle')}</p>
        </div>
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/3865557/pexels-photo-3865557.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
            alt="Spa interior" 
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 md:px-6">
        {/* Our Mission Section */}
        <section className="mb-20">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2 rounded-xl overflow-hidden shadow-xl">
              <img 
                src="https://images.pexels.com/photos/3757952/pexels-photo-3757952.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Spa ambiance" 
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6">{t('about:mission.title')}</h2>
              <p className="text-[#232347]/80 mb-6 leading-relaxed">
                {t('about:mission.paragraph1')}
              </p>
              <p className="text-[#232347]/80 leading-relaxed">
                {t('about:mission.paragraph2')}
              </p>
            </div>
          </div>
        </section>

        {/* Product Philosophy Section */}
        <section className="mb-20">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            <div className="w-full lg:w-1/2 rounded-xl overflow-hidden shadow-xl">
              <img 
                src="https://images.pexels.com/photos/6621374/pexels-photo-6621374.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Spa products" 
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-6">{t('about:philosophy.title')}</h2>
              <p className="text-[#232347]/80 mb-6 leading-relaxed">
                {t('about:philosophy.paragraph1')}
              </p>
              <p className="text-[#232347]/80 mb-6 leading-relaxed">
                {t('about:philosophy.paragraph2')}
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="bg-[#B2D8C5]/20 text-[#232347] px-3 py-1 rounded-full text-sm">{t('about:philosophy.badges.organic')}</span>
                <span className="bg-[#B2D8C5]/20 text-[#232347] px-3 py-1 rounded-full text-sm">{t('about:philosophy.badges.parabenFree')}</span>
                <span className="bg-[#B2D8C5]/20 text-[#232347] px-3 py-1 rounded-full text-sm">{t('about:philosophy.badges.crueltyFree')}</span>
                <span className="bg-[#B2D8C5]/20 text-[#232347] px-3 py-1 rounded-full text-sm">{t('about:philosophy.badges.locallySourced')}</span>
                <span className="bg-[#B2D8C5]/20 text-[#232347] px-3 py-1 rounded-full text-sm">{t('about:philosophy.badges.ecoFriendly')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Meet Our Team Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">{t('about:team.title')}</h2>
          <p className="text-[#232347]/80 mb-12 leading-relaxed text-center max-w-3xl mx-auto">
            {t('about:team.description')}
          </p>
          
          {error && (
            <div className="text-red-500 text-center mb-8 p-4 bg-red-50 rounded-lg border border-red-100">
              {error}
            </div>
          )}
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="inline-block w-12 h-12 border-4 border-[#B2D8C5] border-t-[#232347] rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {team.map((member) => (
                <div key={member.id} className="bg-white/80 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="h-64 overflow-hidden relative">
                    {member.photo ? (
                      <img 
                        src={member.photo} 
                        alt={member.name} 
                        className="w-full h-64 object-cover transition-transform duration-300 hover:scale-105" 
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full bg-gray-100">
                        <div className="text-gray-400">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#232347] mb-1 font-serif">{member.name}</h3>
                    <p className="text-[#B2D8C5] font-medium mb-4">{member.role}</p>
                    <p className="text-[#232347]/70 text-sm">{member.bio}</p>
                  </div>
                </div>
              ))}
              
              {team.length === 0 && !loading && !error && (
                <div className="col-span-full text-center py-12 text-gray-500">
                  No team members found. Check back soon!
                </div>
              )}
            </div>
          )}
        </section>

        {/* Our Journey Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">{t('about:journey.title')}</h2>
          <p className="text-[#232347]/80 mb-12 leading-relaxed text-center max-w-3xl mx-auto">
            {t('about:journey.description')}
          </p>
          
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#B2D8C5] hidden md:block"></div>
            
            {/* Timeline Events */}
            <div className="space-y-12">
              {timeline.map((item, idx) => (
                <div key={idx} className={`flex flex-col md:flex-row ${idx % 2 === 0 ? 'md:flex-row-reverse' : ''} items-center`}>
                  <div className={`md:w-1/2 flex ${idx % 2 === 0 ? 'justify-start' : 'justify-end'} relative`}>
                    <div className="bg-white rounded-xl shadow-md p-6 max-w-md z-10 mb-4 md:mb-0 w-full md:w-auto">
                      <h3 className="text-xl font-semibold text-[#B2D8C5] mb-2">{item.year}</h3>
                      <p className="text-[#232347]/80">{item.event}</p>
                    </div>
                  </div>
                  
                  <div className="md:w-0 relative flex justify-center">
                    <div className="absolute z-20 w-10 h-10 rounded-full bg-[#F7CAC9] border-4 border-white flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    </div>
                  </div>
                  
                  <div className="md:w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Location Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-[#232347] mb-8 text-center">{t('about:location.title')}</h2>
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="w-full lg:w-1/2">
              <div className="bg-white/80 rounded-xl shadow-md p-8">
                <h3 className="text-2xl font-serif font-semibold text-[#232347] mb-4">{t('about:location.name')}</h3>
                <p className="text-[#232347]/80 mb-2">{t('about:location.address1')}</p>
                <p className="text-[#232347]/80 mb-6">{t('about:location.address2')}</p>
                
                <h4 className="text-lg font-semibold text-[#232347] mb-2">{t('about:location.hours')}</h4>
                <div className="grid grid-cols-2 gap-y-2 text-[#232347]/80 mb-6">
                  <span>{t('about:location.schedule.weekdays')}</span>
                  <span>{t('about:location.schedule.weekdaysHours')}</span>
                  <span>{t('about:location.schedule.saturday')}</span>
                  <span>{t('about:location.schedule.saturdayHours')}</span>
                  <span>{t('about:location.schedule.sunday')}</span>
                  <span>{t('about:location.schedule.sundayHours')}</span>
                </div>
                
                <h4 className="text-lg font-semibold text-[#232347] mb-2">{t('about:location.contact')}</h4>
                <p className="text-[#232347]/80 mb-1">{t('about:location.phone')}</p>
                <p className="text-[#232347]/80 mb-4">{t('about:location.email')}</p>
                
                <div className="flex gap-4 mt-6">
                  <a href="https://maps.google.com/?q=6833+Coit+Rd+Suite+104,+Plano,+TX+75024" target="_blank" rel="noopener noreferrer" className="bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-5 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {t('common:buttons.getDirections')}
                  </a>
                  <button className="bg-[#F7CAC9] hover:bg-[#e6b9b8] text-white px-5 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    {t('common:buttons.callNow')}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="w-full lg:w-1/2 rounded-xl overflow-hidden shadow-xl h-[400px]">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3343.5084836697287!2d-96.83058892392145!3d33.05531387357048!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c3d4f2b4f3f0f%3A0x1234567890abcdef!2s4222%20Trinity%20Mills%20Rd%20Suite%20120%2C%20Plano%2C%20TX%2075024!5e0!3m2!1sen!2sus!4v1715649481489!5m2!1sen!2sus" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Healing Lotus Spa Location"
              ></iframe>
            </div>
          </div>
        </section>
      </div>

      {/* Call to Action */}
      <section className="bg-[#232347] text-white py-16 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-4">{t('about:cta.title')}</h2>
          <p className="max-w-2xl mx-auto mb-8 text-white/80">{t('about:cta.description')}</p>
          <button className="bg-[#B2D8C5] hover:bg-[#88bfa3] text-white px-8 py-3 rounded-md font-semibold text-lg transition-colors">{t('about:cta.button')}</button>
        </div>
      </section>
    </div>
  );
}

export default About; 